import React, { useEffect, useState } from 'react';
import { HeroContent } from '@/components/Bloks/Hero/HeroContent';
import { SbSection } from '@/components/Shared/Layout/SbSection';
import { SbImage } from '@/components/Shared/Image/SbImage';
import { isStringWithValue } from '@/utils/helpers/isStringWithValue';
import { IHeroBlok } from '@/utils/interfaces/bloks';
import { GenericPopup } from '@/components/Shared/Modal/GenericPopup';
import { HeroContentWithSearch } from './HeroContentWithSearch';

interface IProps {
  blok: IHeroBlok;
  preview: boolean;
  image?: string;
}

const getEmbedUrl = (url: string): string | null => {
  if (!url) return null;

  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    const videoId = url.split('v=')[1]?.split('&')[0] || url.split('/').pop();
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=1&modestbranding=1&rel=0`;
  } else if (url.includes('vimeo.com')) {
    const videoId = url.split('/').pop();
    return `https://player.vimeo.com/video/${videoId}?autoplay=1&loop=1&controls=1&title=0&byline=0&portrait=0`;
  }

  return null;
};

export const HeroWithSearchField: React.FC<IProps> = ({ blok, preview }) => {
  const mediaSrc = blok.image?.filename || '';
  const altText = blok.image?.alt || '';

  const isUploadedVideo = mediaSrc.endsWith('.mp4');
  const isEmbed =
    mediaSrc.includes('youtube.com') ||
    mediaSrc.includes('youtu.be') ||
    mediaSrc.includes('vimeo.com');
  const embedUrl = isEmbed ? getEmbedUrl(mediaSrc) : null;

  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (embedUrl) {
      setIframeSrc(`${embedUrl}&muted=1&cacheBust=${Date.now()}`);
    }
  }, [embedUrl]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');
      if (anchor && anchor.textContent?.includes('View Interactive Demo')) {
        e.preventDefault();
        setShowModal(true);
      }
    };

    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const isHome = true;

  const getImageUrl = (image: any): string => {
    if (typeof image === 'string') {
      return image;
    }
    if (image && typeof image === 'object' && image.filename) {
      return image.filename;
    }
    return 'https://a.storyblok.com/f/154592/1408x768/282063d48f/herobanner.jpg';
  };

  const backgroundImageUrl = 'https://a.storyblok.com/f/154592/1408x768/282063d48f/herobanner.jpg';

  return (
    <>
      <SbSection
        blok={blok}
        preview={preview}
        classNames={`bg-green-dark text-white bg-no-repeat bg-right-bottom bg-cover py-6 lg:py-12 ${
          isHome ? 'hero-section-search lg:!py-0 max-h-[336px] flex justify-center items-center' : ''
        }`}
        style={{
          backgroundImage: `url(${backgroundImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'right 96%',
          backgroundRepeat: 'no-repeat',
          height: "336px",
        }}
      >
        <div className={` ${isHome ? '' : 'items-start justify-between max-w-7xl mx-auto px-4 sm:px-8 lg:px-8 flex flex-col-reverse lg:flex-row  gap-0 lg:gap-8'}`}>
          <div className={`${isHome ? "" : "w-full lg:py-0 max-w-[900px] lg:w-[55%]"} `}>
            <HeroContentWithSearch blok={blok} isHome />
          </div>
          {!isHome && (
            <div className="w-full flex justify-start sm:justify-start max-[672px]:justify-start max-[672px]:pl-0 lg:w-[55%] xl:w-[70%] lg:mt-6">
              <div className="relative w-full max-w-[450px] sm:max-w-[600px] md:max-w-[700px] lg:max-w-[800px] xl:max-w-[900px] aspect-[16/10]">
                <img
                  src="/Machero.png"
                  alt="Laptop Frame"
                  className="absolute top-0 left-0 w-full h-full z-10 pointer-events-none"
                />
                {isEmbed && iframeSrc ? (
                  <iframe
                    key={iframeSrc}
                    src={iframeSrc}
                    className="absolute top-[15.6%] left-0 w-full h-[55%] z-20"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    title="Embedded Video"
                  />
                ) : isUploadedVideo ? (
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    controls
                    className="absolute top-[15.6%] left-0 w-full h-[55%] z-20 object-cover"
                    aria-label="Uploaded video"
                  >
                    <source src={mediaSrc} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </SbSection>

      <GenericPopup isOpen={showModal} onClose={() => setShowModal(false)}>
        <div className="w-full aspect-[16/9]">
          <iframe
            src="https://app.storylane.io/demo/1xnmy9gevghi?embed=inline"
            className="w-full h-full border-0 rounded-lg shadow-md"
            allow="fullscreen"
          ></iframe>
        </div>
      </GenericPopup>
    </>
  );
};